import { useState, useEffect, useCallback } from "react";
import type { ApiError } from "@/types";

interface AsyncState<T> {
  data: T | null;
  isLoading: boolean;
  error: ApiError | null;
}

/**
 * Generic hook for async data fetching with loading/error states.
 *
 * @param fetcher  – Async function that returns the data.
 * @param autoFetch – If true, fetches immediately on mount. Default: true.
 */
export function useAsync<T>(fetcher: () => Promise<T>, autoFetch = true) {
  const [state, setState] = useState<AsyncState<T>>({
    data: null,
    isLoading: autoFetch,
    error: null,
  });

  useEffect(() => {
    if (!autoFetch) return;

    const run = async () => {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      try {
        const data = await fetcher();
        setState({ data, isLoading: false, error: null });
      } catch (err) {
        // Type guard: check if error is an ApiError object (has status field)
        // Otherwise fallback to generic error to ensure type safety
        const apiError: ApiError =
          typeof err === "object" && err !== null && "status" in err
            ? (err as ApiError)
            : { status: 0, message: "Error desconocido" };

        setState({ data: null, isLoading: false, error: apiError });
      }
    };

    run();
  }, [autoFetch, fetcher]);

  const execute = useCallback(async () => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const data = await fetcher();
      setState({ data, isLoading: false, error: null });
    } catch (err) {
      const apiError: ApiError =
        typeof err === "object" && err !== null && "status" in err
          ? (err as ApiError)
          : { status: 0, message: "Error desconocido" };

      setState({ data: null, isLoading: false, error: apiError });
    }
  }, [fetcher]);

  return { ...state, refetch: execute };
}
